import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { AIMessage, Conversation } from '../../types';
import { fetchSessions, fetchSessionMessages, deleteSession, renameSession, GLOBAL_CHAT_BOOK_ID } from '../../api/aiSessionClient';
import { dataCache } from '../../cache/DataCache';

export { GLOBAL_CHAT_BOOK_ID };

export function chatConvKey(bookId: string) { return 'ai-chat-conv-' + bookId; }

// 会话时间标签：实现收敛到 utils/displayFormat（唯一定义）。
// 此前这里、chat-model-pref.ts、WritingMode.tsx 各有一份逐字相同的副本，
// 但只有一份有测试覆盖 —— 改一处漏一处不会有任何提示。
export { formatShortDate } from '../../utils/displayFormat';

interface AIChatContextValue {
  bookId: string;
  selectedConvId: string | null;
  isNewConvMode: boolean;
  chatConvs: Conversation[];
  chatHistory: AIMessage[];
  setChatHistory: React.Dispatch<React.SetStateAction<AIMessage[]>>;
  agentStatus: string | null;
  setAgentStatus: React.Dispatch<React.SetStateAction<string | null>>;
  externalChatConvId: string | null;
  setExternalChatConvId: React.Dispatch<React.SetStateAction<string | null>>;
  showWelcome: boolean;
  contentKey: string;
  chatLoading: boolean;
  handleSelectConv: (convId: string) => void;
  handleNewConv: () => void;
  handleExitConversation: () => void;
  handleConvCreated: (convId: string) => void;
  handleDeleteConv: (convId: string, e: React.MouseEvent) => void;
  handleConversationsUpdated: () => void;
  /** 重命名会话（同步 localStorage 与服务端） */
  renameConv: (convId: string, newTitle: string) => Promise<void>;
}

const AIChatContext = createContext<AIChatContextValue | null>(null);

export function useAIChat(): AIChatContextValue {
  const ctx = useContext(AIChatContext);
  if (!ctx) throw new Error('useAIChat must be used within an AIChatProvider');
  return ctx;
}

export function AIChatProvider({ children }: { children: React.ReactNode }) {
  const bookId = GLOBAL_CHAT_BOOK_ID;

  const [selectedConvId, setSelectedConvId] = useState<string | null>(null);
  const [isNewConvMode, setIsNewConvMode] = useState(false);
  const [convRefreshKey, setConvRefreshKey] = useState(0);
  const [contentKeyId, setContentKeyId] = useState<string>('new');
  const [chatHistory, setChatHistory] = useState<AIMessage[]>([]);
  const [agentStatus, setAgentStatus] = useState<string | null>(null);
  const [externalChatConvId, setExternalChatConvId] = useState<string | null>(null);
  const [chatLoading, setChatLoading] = useState(false);
  const [chatConvs, setChatConvs] = useState<Conversation[]>(() => dataCache.getAISessions(bookId) ?? []);

  // 从服务端加载全部普通模式会话（仅元数据，不含消息内容）
  useEffect(() => {
    const loadConvs = async () => {
      try {
        const sessions = await fetchSessions('', 'normal', 'meta');
        const convs = sessions.map((s) => ({
          id: s.id,
          title: s.title,
          createdAt: s.createdAt,
          messages: s.messages as Conversation['messages'],
          mode: (s.mode ?? 'normal') as Conversation['mode'],
          bookId: s.bookId,
          costCny: s.costCny ?? null,
          totalTokens: s.totalTokens ?? 0,
        }));
        const sorted = convs.sort((a, b) => b.createdAt - a.createdAt);
        if (sorted.length > 0) {
          dataCache.setAISessions(bookId, sorted);
          setChatConvs(sorted);
          localStorage.setItem(chatConvKey(bookId), JSON.stringify(sorted));
        } else {
          const raw = localStorage.getItem(chatConvKey(bookId));
          const localConvs = raw ? (JSON.parse(raw) as Conversation[]) : [];
          if (localConvs.length > 0) {
            dataCache.setAISessions(bookId, localConvs);
            setChatConvs(localConvs);
            import('../../api/aiSessionClient').then(({ upsertSession }) => {
              localConvs.forEach((c) => {
                upsertSession({
                  session_uuid: c.id,
                  book_id: c.bookId || bookId,
                  mode: c.mode ?? 'normal',
                  title: c.title,
                  messages: c.messages,
                }).catch(() => { /* ignore */ });
              });
            });
          } else {
            setChatConvs([]);
          }
        }
      } catch {
        try {
          const raw = localStorage.getItem(chatConvKey(bookId));
          const convs = raw ? (JSON.parse(raw) as Conversation[]) : [];
          const sorted = convs.sort((a, b) => b.createdAt - a.createdAt);
          dataCache.setAISessions(bookId, sorted);
          setChatConvs(sorted);
        } catch { setChatConvs([]); }
      }
    };
    loadConvs();
  }, [bookId, convRefreshKey]);

  const handleSelectConv = useCallback((convId: string) => {
    setIsNewConvMode(false);
    setSelectedConvId(convId);
    setContentKeyId(convId);
    setExternalChatConvId(convId);
    setChatLoading(true);
    fetchSessionMessages(convId).then((messages) => {
      setChatHistory(messages as AIMessage[]);
    }).catch(() => setChatHistory([])).finally(() => setChatLoading(false));
  }, []);

  const handleNewConv = useCallback(() => {
    setSelectedConvId(null);
    setIsNewConvMode(true);
    setContentKeyId('new');
    setExternalChatConvId(null);
    setChatHistory([]);
  }, []);

  const handleExitConversation = useCallback(() => {
    setSelectedConvId(null);
    setIsNewConvMode(false);
    setContentKeyId('new');
    setExternalChatConvId(null);
    setChatHistory([]);
  }, []);

  const handleConvCreated = useCallback((convId: string) => {
    setSelectedConvId(convId);
    setIsNewConvMode(false);
    setConvRefreshKey(k => k + 1);
  }, []);

  const handleDeleteConv = useCallback((convId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    deleteSession(convId).catch(() => { /* 服务端删除失败，localStorage 兜底 */ });
    try {
      const convs = JSON.parse(localStorage.getItem(chatConvKey(bookId)) ?? '[]') as Conversation[];
      localStorage.setItem(chatConvKey(bookId), JSON.stringify(convs.filter(c => c.id !== convId)));
    } catch { /* ignore */ }
    if (selectedConvId === convId) {
      setSelectedConvId(null);
      setIsNewConvMode(false);
      setExternalChatConvId(null);
      setChatHistory([]);
    }
    dataCache.invalidateAISessions(bookId);
    setConvRefreshKey(k => k + 1);
  }, [bookId, selectedConvId]);

  const handleConversationsUpdated = useCallback(() => {
    setConvRefreshKey(k => k + 1);
  }, []);

  const renameConv = useCallback(async (convId: string, newTitle: string) => {
    const title = newTitle.trim();
    if (!title) return;
    const conv = chatConvs.find(c => c.id === convId);
    try {
      await renameSession(convId, title, conv?.bookId ?? GLOBAL_CHAT_BOOK_ID);
    } catch { /* 服务端失败时 localStorage 兜底 */ }
    try {
      const convs = JSON.parse(localStorage.getItem(chatConvKey(bookId)) ?? '[]') as Conversation[];
      localStorage.setItem(chatConvKey(bookId), JSON.stringify(convs.map(c => c.id === convId ? { ...c, title } : c)));
    } catch { /* ignore */ }
    setChatConvs(prev => prev.map(c => c.id === convId ? { ...c, title } : c));
    dataCache.invalidateAISessions(bookId);
  }, [bookId, chatConvs]);

  const showWelcome = selectedConvId === null && !isNewConvMode;
  const contentKey = `chat-${contentKeyId}`;

  const value: AIChatContextValue = {
    bookId,
    selectedConvId,
    isNewConvMode,
    chatConvs,
    chatHistory,
    setChatHistory,
    agentStatus,
    setAgentStatus,
    externalChatConvId,
    setExternalChatConvId,
    showWelcome,
    contentKey,
    chatLoading,
    handleSelectConv,
    handleNewConv,
    handleExitConversation,
    handleConvCreated,
    handleDeleteConv,
    handleConversationsUpdated,
    renameConv,
  };

  return (
    <AIChatContext.Provider value={value}>
      {children}
    </AIChatContext.Provider>
  );
}
